// Feature: M004-F0717 (mode A of the Calendar tab). Tests: T-M004-0217, T-M004-0218, live T-M004-0224.
//
// The TRANSACTION calendar (design M004-F0712-F0716 section 15): the Bookings -> Calendar tab renders transactions by
// room and date from listBillingTransactions (Spec core-ledger-account-gateway 1.4.0, Core #310), one entry per
// transaction with its TX chip and stage. Mounted by BookingsCalendar.js ONLY when the selected organisation's
// my-permissions has billing_view === true (mode A, design 15.7). Mode B (everyone else) is the unchanged bookings
// calendar; the transactions endpoint is never called there and is never probed to pick the mode.
//
// - NO "picked up from Google" indicator and no origin text on an entry: its presence is the confirmation; provenance
//   is the first `captured` event in the drawer's History tab (owner correction 2026-10-06).
// - Entries are placed by the wall-clock `window` digits, never converted to the viewer's zone (ACCT-TX-012).
// - The chip is a SIBLING of the entry button, never nested in it.
// - A failure on the transactions read (a 403 included) is an error state shown plainly: never a silent switch to the
//   bookings grid and never a blank grid.
import React, { useEffect, useMemo, useState } from 'react';
import api, { bookingApi, roomApi } from '../../services/api';
import { buildTxCalendarApi, readTransactionWindow } from '../../services/txCalendarAdapter';
import TxChip from '../TxChip';
import { amountText, conditionBadge, stageLabel } from '../tx/txStages';
import BookingDrawer from './BookingDrawer';
import { readWholeCalendarWindow } from './bookingsCalendarFetch';
import {
  addMonths, addWeeks, expandBookingsForRange, localDayKey, monthGridRangeOf, weekRangeOf,
} from './bookingsCalendarModel';
import {
  buildTxMonthView, buildTxWeekView, entryTimeRange, rfc3339DayBoundary, splitPlacement, wallClockToDate,
} from './txCalendarModel';
import './BookingsCalendar.css';
import './TransactionCalendar.css';

const VIEW_WEEK = 'week';
const VIEW_MONTH = 'month';
const responseList = (response) => response?.data?.data ?? response?.data ?? [];
const txApi = buildTxCalendarApi(api);

export const CAPPED_NOTICE = (count) => `Showing the first ${count} transactions of this period. Narrow the view (switch to Week) to see the rest.`;
export const NO_TX_NOTICE = (count) => `${count} booking${count === 1 ? '' : 's'} in this period ${count === 1 ? 'has' : 'have'} no transaction number yet. ${count === 1 ? 'It is' : 'They are'} listed in Studio.`;
export const EMPTY_TEXT = 'No transactions in this period.';
export const ERROR_TEXT = 'Could not load the transactions for this organisation.';
export const ERROR_REFUSED_TEXT = 'Could not load the transactions for this organisation. Core refused the request.';
export const NOT_ON_GRID_TITLE = 'Not on the calendar grid';

const rangeOf = (view, anchor) => (view === VIEW_MONTH ? monthGridRangeOf(anchor) : weekRangeOf(anchor));

// The renderer registry keyed by `kind` (design 15.6). booking_occurrence is the full entry; every other kind, and an
// unknown one, is the generic entry (title, window if any, chip, stage), so a reserved kind needs no code change.
const KIND_RENDERERS = Object.freeze({ booking_occurrence: { showSeries: true } });
const rendererFor = (kind) => KIND_RENDERERS[kind] || { showSeries: false };

const EntryFacts = ({ entry }) => {
  const kind = rendererFor(entry.kind);
  const amount = entry.amount ? amountText(entry.amount.minor, entry.amount.currency) : '';
  return (
    <>
      <span className="bkh-tx-stage status-badge" data-testid="tx-cal-stage" data-stage={entry.stage}>{stageLabel(entry.stage, entry.stage_detail)}</span>
      {entry.condition ? <span className="bkh-tx-condition" data-testid="tx-cal-condition" data-code={entry.condition}>{conditionBadge(entry.condition)}</span> : null}
      {amount ? <span className="bkh-tx-amount" data-testid="tx-cal-amount">{amount}</span> : null}
      {entry.instance?.is_exception ? <span className="bkh-tx-changed" data-testid="tx-cal-changed">changed</span> : null}
      {kind.showSeries && entry.series ? (
        <span className="bkh-tx-series" data-testid="tx-cal-series" title={entry.series.recurrence_summary}>Part of a series</span>
      ) : null}
    </>
  );
};

// The entry button and its chip: siblings. The chip is copy-only (the global history drawer is not opened from here):
// its click copies the id, and the wrapper asks the host to open the booking drawer on its History tab (design 15.3).
const EntryControls = ({ occurrence, onOpen }) => {
  const { entry } = occurrence;
  return (
    <>
      <button
        type="button"
        className="bkh-tx-entry"
        data-testid="tx-cal-entry"
        data-tx-id={entry.tx_id}
        data-booking-id={entry.booking_id ?? ''}
        data-stage={entry.stage}
        data-kind={entry.kind}
        title={`${entry.title} ${entryTimeRange(entry)}${entry.room ? ` · ${entry.room.name}` : ''}`}
        onClick={() => onOpen(occurrence, null)}
      >
        <strong>{entry.title || 'Untitled'}</strong>
        <span>{entryTimeRange(entry)}</span>
        <EntryFacts entry={entry} />
      </button>
      <span className="bkh-tx-chip-slot" onClick={() => onOpen(occurrence, 'history')} role="presentation">
        <TxChip handOffId={entry.hand_off_id} txId={entry.tx_id} copyOnly />
      </span>
    </>
  );
};

// The Booking tab's join (design 15.4): the booking occurrence of the entry's booking. The entry's window is the room's
// wall clock and the bookings read is expanded in the browser's zone, so the join never compares clock digits across
// zones: one occurrence of that booking in the visible range is THE one; several (a series) are told apart by the day.
const joinOccurrence = (occurrence, bookingOccurrences) => {
  const bookingId = occurrence.entry.booking_id;
  if (bookingId === null || bookingId === undefined) return null;
  const candidates = bookingOccurrences.filter((candidate) => String(candidate.bookingId) === String(bookingId));
  if (candidates.length === 1) return candidates[0];
  return candidates.find((candidate) => localDayKey(candidate.start) === occurrence.dayKey) || null;
};

const TransactionCalendar = ({ selectedOrgId, organisationName, permissions, planSelectorSlot }) => {
  const [view, setView] = useState(VIEW_WEEK);
  const [anchorDate, setAnchorDate] = useState(() => new Date());
  const [entries, setEntries] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [capped, setCapped] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [drawer, setDrawer] = useState(null);
  const [selectedDayKey, setSelectedDayKey] = useState('');

  useEffect(() => {
    let current = true;
    if (!selectedOrgId) {
      setEntries([]); setRooms([]); setBookings(null); setLoading(false); setError(''); setCapped(false);
      return () => { current = false; };
    }
    setLoading(true);
    setError('');
    setCapped(false);
    const { rangeStart, rangeEndExclusive } = rangeOf(view, anchorDate);
    const from = rfc3339DayBoundary(rangeStart, -1);
    const to = rfc3339DayBoundary(rangeEndExclusive, 1);
    Promise.all([
      readTransactionWindow({ orgId: selectedOrgId, from, to }, txApi)
        .then((read) => ({ read }), (failure) => ({ failure })),
      roomApi.getRooms(selectedOrgId).catch(() => null),
      // The bookings window is read ONLY for the drawer's Booking tab join and the no-transaction guard (design 15.4,
      // 15.5). A failure here never fails the calendar: nothing joins, and the guard stays silent.
      readWholeCalendarWindow(
        { org: selectedOrgId, start: localDayKey(rangeStart), end: localDayKey(rangeEndExclusive) },
        (params) => bookingApi.getAllBookings(params),
      ).then((windowRead) => (windowRead.complete ? windowRead.rows : null), () => null),
    ]).then(([tx, roomsResponse, bookingRows]) => {
      if (!current) return;
      if (tx.failure) {
        const status = Number(tx.failure?.response?.status) || 0;
        setError(status === 403 ? ERROR_REFUSED_TEXT : ERROR_TEXT);
        setEntries([]);
      } else {
        setEntries(tx.read.entries);
        setCapped(tx.read.capped);
      }
      setRooms(responseList(roomsResponse));
      setBookings(bookingRows);
      setLoading(false);
    });
    return () => { current = false; };
  }, [selectedOrgId, view, anchorDate, reloadTick]);

  const { placed, unplaced } = useMemo(() => splitPlacement(entries), [entries]);
  const weekView = useMemo(() => buildTxWeekView({ occurrences: placed, rooms, anchor: anchorDate }), [placed, rooms, anchorDate]);
  const monthView = useMemo(() => buildTxMonthView({ occurrences: placed, anchor: anchorDate }), [placed, anchorDate]);
  const selectedCell = monthView.cells.find((cell) => cell.dayKey === selectedDayKey) || null;

  const { rangeStart, rangeEndExclusive } = rangeOf(view, anchorDate);
  const bookingOccurrences = useMemo(
    () => (bookings ? expandBookingsForRange(bookings, rangeStart, rangeEndExclusive) : []),
    [bookings, rangeStart, rangeEndExclusive],
  );
  // The guard (design 15.5): only n > 0 renders, and NOTHING renders at 0.
  const withoutTx = useMemo(() => {
    if (!bookings) return 0;
    // Per booking: its occurrences in the range minus the transactions that name it. Counted, never compared by clock
    // digits, so the browser's zone cannot invent a missing transaction.
    const txPerBooking = new Map();
    entries.forEach((entry) => {
      if (entry.booking_id != null) txPerBooking.set(String(entry.booking_id), (txPerBooking.get(String(entry.booking_id)) || 0) + 1);
    });
    const occurrencesPerBooking = new Map();
    bookingOccurrences.forEach((o) => {
      if (o.bookingId == null || ['cancelled', 'rejected'].includes(o.statusKey)) return;
      occurrencesPerBooking.set(String(o.bookingId), (occurrencesPerBooking.get(String(o.bookingId)) || 0) + 1);
    });
    let missing = 0;
    occurrencesPerBooking.forEach((count, id) => { missing += Math.max(0, count - (txPerBooking.get(id) || 0)); });
    return missing;
  }, [bookings, bookingOccurrences, entries]);

  const shift = (direction) => {
    setAnchorDate((current) => (view === VIEW_WEEK ? addWeeks(current, direction) : addMonths(current, direction)));
    setSelectedDayKey('');
  };

  const openEntry = (occurrence, initialTab) => {
    setDrawer({ occurrence, initialTab, joined: joinOccurrence(occurrence, bookingOccurrences) });
  };

  const drawerOccurrence = drawer ? (drawer.joined || {
    key: drawer.occurrence.key,
    title: drawer.occurrence.title,
    roomName: drawer.occurrence.roomName,
    start: drawer.occurrence.start || wallClockToDate(drawer.occurrence.entry.window?.start),
    end: drawer.occurrence.end,
    statusText: '',
    statusBadgeClass: 'status-badge',
    sourceBooking: {},
    bookingId: null,
  }) : null;

  const nothing = !loading && !error && entries.length === 0;
  const showGrid = !loading && !error && entries.length > 0;

  return (
    <div className="bkh-calendar" data-testid="bookings-hub-calendar" data-mode="transactions">
      <div className="bkh-cal-toolbar">
        <div className="bkh-cal-toolbar-controls">
          <div className="bkh-cal-view-toggle" role="group" aria-label="Calendar view">
            <button type="button" className={`bkh-cal-view-btn${view === VIEW_WEEK ? ' active' : ''}`} data-testid="bkh-cal-view-week" aria-pressed={view === VIEW_WEEK} onClick={() => { setView(VIEW_WEEK); setSelectedDayKey(''); }}>Week</button>
            <button type="button" className={`bkh-cal-view-btn${view === VIEW_MONTH ? ' active' : ''}`} data-testid="bkh-cal-view-month" aria-pressed={view === VIEW_MONTH} onClick={() => setView(VIEW_MONTH)}>Month</button>
          </div>
          <button type="button" className="btn btn-outline btn-small" data-testid="bkh-cal-prev" aria-label="Previous" onClick={() => shift(-1)}>‹</button>
          <button type="button" className="btn btn-outline btn-small" data-testid="bkh-cal-today" onClick={() => { setAnchorDate(new Date()); setSelectedDayKey(''); }}>Today</button>
          <button type="button" className="btn btn-outline btn-small" data-testid="bkh-cal-next" aria-label="Next" onClick={() => shift(1)}>›</button>
          <span className="bkh-cal-range" data-testid="bkh-cal-range">{view === VIEW_WEEK ? weekView.rangeLabel : monthView.rangeLabel}</span>
        </div>
      </div>

      {error ? (
        <p className="bkh-error" role="alert" data-testid="tx-cal-error">
          {error}{' '}
          <button type="button" className="btn btn-outline btn-sm" data-testid="tx-cal-retry" onClick={() => setReloadTick((tick) => tick + 1)}>Try again</button>
        </p>
      ) : null}
      {loading ? <p className="bkh-loading" data-testid="bkh-cal-loading">Loading…</p> : null}
      {!loading && !error && capped ? (
        <p className="bkh-error" role="status" data-testid="tx-cal-capped">{CAPPED_NOTICE(entries.length)}</p>
      ) : null}
      {!loading && !error && withoutTx > 0 ? (
        <p className="bkh-error" role="status" data-testid="tx-cal-untracked" data-count={withoutTx}>{NO_TX_NOTICE(withoutTx)}</p>
      ) : null}
      {nothing ? <p className="bkh-empty" data-testid="tx-cal-empty">{EMPTY_TEXT}</p> : null}

      {showGrid && view === VIEW_WEEK ? (
        <div className="bkh-cal-week" data-testid="bkh-cal-week-grid">
          <div className="bkh-cal-week-head" aria-hidden="true">
            <span className="bkh-cal-lane-label" />
            {weekView.days.map((day, index) => (
              <span key={day.getTime()} className="bkh-cal-day-head"><strong>{['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][index]}</strong>{` ${day.getDate()}`}</span>
            ))}
          </div>
          {weekView.lanes.map((lane) => (
            <div key={lane.key} className="bkh-cal-lane" data-testid="bkh-cal-lane" data-room-name={lane.label}>
              <span className="bkh-cal-lane-label" title={lane.label}>{lane.label}</span>
              <div className="bkh-cal-lane-days">
                {lane.cells.map((cell) => (
                  <div key={cell.dayKey} className="bkh-cal-day-cell" data-day-key={cell.dayKey}>
                    {cell.blocks.map((block) => (
                      <div
                        key={block.occurrence.key}
                        className="bkh-cal-block bkh-tx-block"
                        data-testid="tx-cal-block"
                        style={{ top: `${block.topPct}%`, height: `${block.heightPct}%`, left: `${block.leftPct}%`, width: `${block.widthPct}%` }}
                      >
                        <EntryControls occurrence={block.occurrence} onOpen={openEntry} />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ))}
          {weekView.count === 0 && unplaced.length === 0 ? <p className="bkh-empty" data-testid="tx-cal-week-empty">{EMPTY_TEXT}</p> : null}
        </div>
      ) : null}

      {showGrid && view === VIEW_MONTH ? (
        <>
          <div className="bkh-cal-month" data-testid="bkh-cal-month-grid">
            {monthView.weekdayHeaders.map((header) => <span key={header} className="bkh-cal-month-head">{header}</span>)}
            {monthView.cells.map((cell) => (
              <button
                key={cell.dayKey}
                type="button"
                className={`bkh-cal-month-cell${cell.inMonth ? '' : ' outside'}${cell.dayKey === selectedDayKey ? ' selected' : ''}`}
                data-testid="bkh-cal-day-cell"
                data-day-key={cell.dayKey}
                data-count={cell.count}
                aria-label={`${cell.day.getDate()} ${monthView.rangeLabel}: ${cell.count} transaction${cell.count === 1 ? '' : 's'}`}
                onClick={() => setSelectedDayKey(cell.count > 0 ? cell.dayKey : '')}
              >
                <span className="bkh-cal-month-date">{cell.day.getDate()}</span>
                {cell.count > 0 ? (
                  <>
                    <span className="bkh-cal-day-count" data-testid="bkh-cal-day-count">{cell.count}</span>
                    <span className="bkh-cal-dots">
                      {cell.families.map((family) => (
                        <span key={family.key} className={`bkh-tx-dot bkh-tx-dot-${family.key}`} data-testid="tx-cal-dot" data-family={family.key} title={`${family.count} ${family.label}`} />
                      ))}
                    </span>
                  </>
                ) : null}
              </button>
            ))}
          </div>
          {selectedCell ? (
            <div className="bkh-cal-day-list" data-testid="bkh-cal-day-list">
              <h3>{selectedCell.day.getDate()} {monthView.rangeLabel} — {selectedCell.count} transaction{selectedCell.count === 1 ? '' : 's'}</h3>
              {selectedCell.occurrences.map((occurrence) => (
                <div key={occurrence.key} className="bkh-cal-day-item-row bkh-tx-block-row" data-testid="tx-cal-day-item">
                  <EntryControls occurrence={occurrence} onOpen={openEntry} />
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : null}

      {!loading && !error && unplaced.length > 0 ? (
        <section className="bkh-tx-unplaced" data-testid="tx-cal-unplaced" aria-label={NOT_ON_GRID_TITLE}>
          <h3>{NOT_ON_GRID_TITLE}</h3>
          {unplaced.map((occurrence) => (
            <div key={occurrence.key} className="bkh-tx-block-row" data-testid="tx-cal-unplaced-item">
              <EntryControls occurrence={occurrence} onOpen={openEntry} />
            </div>
          ))}
        </section>
      ) : null}

      {drawer ? (
        <BookingDrawer
          occurrence={drawerOccurrence}
          onClose={() => setDrawer(null)}
          permissions={permissions}
          planSelectorSlot={planSelectorSlot}
          organisationId={selectedOrgId}
          transaction={{
            hand_off_id: drawer.occurrence.entry.hand_off_id,
            tx_id: drawer.occurrence.entry.tx_id,
            orgId: selectedOrgId,
            initialTab: drawer.initialTab,
            bookingUnavailable: !drawer.joined,
          }}
        />
      ) : null}
    </div>
  );
};

export default TransactionCalendar;
